import api from './api';

export type ProductType = 'course' | 'catalog' | 'bundle' | 'library_item';
export type ProductVisibility = 'public' | 'private' | 'unlisted';
export type PriceType = 'one_time' | 'subscription' | 'free';
export type PriceInterval = 'month' | 'year';

export interface ProductPrice {
    id: number;
    priceType: PriceType;
    amount: string;
    currency: string;
    interval: PriceInterval | null;
    intervalCount: number | null;
    trialDays: number | null;
    isDefault: boolean;
    isActive: boolean;
    validFrom: string | null;
    validUntil: string | null;
    metadata: Record<string, unknown> | null;
    createdAt: string;
    updatedAt: string;
}

export interface Product {
    id: number;
    uuid: string;
    companyId: number | null;
    type: ProductType;
    referenceId: number | null;
    title: string;
    description: string | null;
    shortDescription: string | null;
    thumbnailUrl: string | null;
    isActive: boolean;
    isPublic: boolean;
    visibility: ProductVisibility;
    metadata: Record<string, unknown> | null;
    createdBy: number;
    createdAt: string;
    updatedAt: string;
    prices: ProductPrice[];
}

export interface ProductListResponse {
    products: Product[];
    total: number;
}

export interface CreateProductData {
    type: ProductType;
    referenceId?: number;
    title: string;
    description?: string;
    shortDescription?: string;
    thumbnailUrl?: string;
    isActive?: boolean;
    isPublic?: boolean;
    visibility?: ProductVisibility;
    metadata?: Record<string, unknown>;
}

export interface UpdateProductData {
    title?: string;
    description?: string;
    shortDescription?: string;
    thumbnailUrl?: string;
    isActive?: boolean;
    isPublic?: boolean;
    visibility?: ProductVisibility;
    metadata?: Record<string, unknown>;
}

export interface CreatePriceData {
    priceType: PriceType;
    amount: string;
    currency?: string;
    interval?: PriceInterval;
    intervalCount?: number;
    trialDays?: number;
    isDefault?: boolean;
    isActive?: boolean;
    validFrom?: string;
    validUntil?: string;
    metadata?: Record<string, unknown>;
}

export interface UpdatePriceData {
    amount?: string;
    currency?: string;
    interval?: PriceInterval;
    intervalCount?: number;
    trialDays?: number;
    isDefault?: boolean;
    isActive?: boolean;
    validFrom?: string;
    validUntil?: string;
    metadata?: Record<string, unknown>;
}

export interface ProductFilters {
    type?: ProductType;
    visibility?: ProductVisibility;
    isActive?: boolean;
    isPublic?: boolean;
    search?: string;
    page?: number;
    itemsPerPage?: number;
}

interface HydraCollection<T> {
    member?: T[];
    'hydra:member'?: T[];
    totalItems?: number;
    'hydra:totalItems'?: number;
}

const LD_JSON = { headers: { Accept: 'application/ld+json' } };
const LD_JSON_WRITE = { headers: { Accept: 'application/ld+json', 'Content-Type': 'application/ld+json' } };
const MERGE_PATCH = {
    headers: { Accept: 'application/ld+json', 'Content-Type': 'application/merge-patch+json' },
};

function collectionMembers<T>(data: HydraCollection<T>): T[] {
    return data.member ?? data['hydra:member'] ?? [];
}

function collectionTotal<T>(data: HydraCollection<T>): number {
    return data.totalItems ?? data['hydra:totalItems'] ?? 0;
}

class ProductService {
    /**
     * Get all products with optional filters
     */
    async getProducts(filters?: ProductFilters): Promise<{ products: Product[]; total: number }> {
        const params = new URLSearchParams();

        if (filters?.type) params.append('type', filters.type);
        if (filters?.visibility) params.append('visibility', filters.visibility);
        if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
        if (filters?.isPublic !== undefined) params.append('isPublic', String(filters.isPublic));
        if (filters?.search) params.append('title', filters.search);
        if (filters?.page) params.append('page', String(filters.page));
        if (filters?.itemsPerPage) params.append('itemsPerPage', String(filters.itemsPerPage));

        const query = params.toString();
        const response = await api.get<HydraCollection<Product>>(
            `/products${query ? `?${query}` : ''}`,
            LD_JSON
        );

        return {
            products: collectionMembers(response.data),
            total: collectionTotal(response.data),
        };
    }

    /**
     * Get a single product by ID
     */
    async getProduct(id: number): Promise<Product> {
        const response = await api.get<Product>(`/products/${id}`, LD_JSON);
        return response.data;
    }

    /**
     * Get public products for storefront
     */
    async getPublicProducts(companyId?: number): Promise<Product[]> {
        const params = new URLSearchParams();
        params.append('isActive', 'true');
        params.append('isPublic', 'true');
        params.append('visibility', 'public');
        if (companyId) params.append('companyId', String(companyId));

        const response = await api.get<HydraCollection<Product>>(
            `/products?${params.toString()}`,
            LD_JSON
        );

        return collectionMembers(response.data);
    }

    /**
     * Create a new product
     */
    async createProduct(data: CreateProductData): Promise<Product> {
        const response = await api.post<Product>('/products', data, LD_JSON_WRITE);
        return response.data;
    }

    /**
     * Update an existing product
     */
    async updateProduct(id: number, data: UpdateProductData): Promise<Product> {
        const response = await api.patch<Product>(`/products/${id}`, data, MERGE_PATCH);
        return response.data;
    }

    /**
     * Delete a product
     */
    async deleteProduct(id: number): Promise<void> {
        await api.delete(`/products/${id}`, LD_JSON);
    }

    /**
     * Get prices for a product
     */
    async getProductPrices(productId: number): Promise<ProductPrice[]> {
        const response = await api.get<HydraCollection<ProductPrice>>(
            `/product_prices?product=${productId}`,
            LD_JSON
        );
        return collectionMembers(response.data);
    }

    /**
     * Create a price for a product
     */
    async createPrice(productId: number, data: CreatePriceData): Promise<ProductPrice> {
        const response = await api.post<ProductPrice>('/product_prices', {
            ...data,
            product: `/api/products/${productId}`,
        }, LD_JSON_WRITE);
        return response.data;
    }

    /**
     * Update a price
     */
    async updatePrice(priceId: number, data: UpdatePriceData): Promise<ProductPrice> {
        const response = await api.patch<ProductPrice>(`/product_prices/${priceId}`, data, MERGE_PATCH);
        return response.data;
    }

    /**
     * Delete a price
     */
    async deletePrice(priceId: number): Promise<void> {
        await api.delete(`/product_prices/${priceId}`, LD_JSON);
    }

    /**
     * Create product from course
     */
    async createProductFromCourse(
        courseId: number,
        title: string,
        data?: Partial<CreateProductData>
    ): Promise<Product> {
        if (!title.trim()) {
            throw new Error('Ein Produkt braucht einen Titel.');
        }

        return this.createProduct({
            type: 'course',
            referenceId: courseId,
            title: title.trim(),
            description: data?.description,
            shortDescription: data?.shortDescription,
            thumbnailUrl: data?.thumbnailUrl,
            isActive: data?.isActive ?? true,
            isPublic: data?.isPublic ?? false,
            visibility: data?.visibility ?? 'private',
        });
    }

    /**
     * Get default price for a product
     */
    getDefaultPrice(product: { prices?: ProductPrice[] }): ProductPrice | null {
        if (!product.prices || product.prices.length === 0) return null;

        const defaultPrice = product.prices.find(p => p.isDefault && p.isActive);
        if (defaultPrice) return defaultPrice;

        const activePrice = product.prices.find(p => p.isActive);
        return activePrice || null;
    }

    /**
     * Format price for display
     */
    formatPrice(price: ProductPrice): string {
        if (price.priceType === 'free' || parseFloat(price.amount) === 0) {
            return 'Kostenlos';
        }

        const amount = parseFloat(price.amount);
        const formatted = new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: price.currency || 'EUR',
        }).format(amount);

        if (price.priceType === 'subscription' && price.interval) {
            const intervalLabel = price.intervalCount && price.intervalCount > 1
                ? `${price.intervalCount} ${price.interval === 'month' ? 'Monate' : 'Jahre'}`
                : price.interval === 'month' ? 'Monat' : 'Jahr';
            return `${formatted} / ${intervalLabel}`;
        }

        return formatted;
    }

    /**
     * Check if product is free
     */
    isProductFree(product: Product): boolean {
        const defaultPrice = this.getDefaultPrice(product);
        if (!defaultPrice) return false;
        return defaultPrice.priceType === 'free' || parseFloat(defaultPrice.amount) === 0;
    }

    /**
     * Check if product has subscription pricing
     */
    hasSubscription(product: Product): boolean {
        return product.prices?.some(p => p.priceType === 'subscription' && p.isActive) || false;
    }
}

export const productService = new ProductService();
export default productService;
